/**
 * Object Tree Panel — Hierarchical tree (left) + Properties (right)
 */
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Input } from '@/components/ui/input';
import { Box, Search, Loader2, Crosshair, LocateFixed, Download } from 'lucide-react';
import { ObjectTreeNode } from './ObjectTree';
import { toast } from 'sonner';

const API = process.env.REACT_APP_BACKEND_URL;

const STATUS_COLORS = {
  COMPLETED: '#10B981', AHEAD: '#22C55E', ON_TRACK: '#3B82F6',
  BEHIND: '#F59E0B', CRITICAL: '#EF4444', NOT_STARTED: '#6B7280', IN_PROGRESS: '#3B82F6',
};

function ObjectProperties({ detail }) {
  return (
    <div className="space-y-3">
      <div className="flex items-center gap-2">
        <Box className="w-4 h-4 text-blue-400" />
        <h4 className="font-semibold text-sm truncate">{detail.name || `Object ${detail.dbId}`}</h4>
      </div>
      <div className="space-y-1 text-xs">
        <PropRow label="dbId" value={<span className="font-mono">{detail.dbId}</span>} />
        {detail.externalId && <PropRow label="External ID" value={<span className="font-mono text-[10px] truncate max-w-[160px]" title={detail.externalId}>{detail.externalId}</span>} />}
        {detail.depth != null && <PropRow label="Depth" value={detail.depth} />}
        {detail.childCount != null && <PropRow label="Children" value={detail.childCount} />}
      </div>
      {detail.properties && Object.keys(detail.properties).length > 0 && (
        <div className="mt-3">
          <h5 className="text-xs font-semibold mb-2 text-muted-foreground uppercase tracking-wide">Properties</h5>
          {Object.entries(detail.properties).map(([cat, props]) => (
            <div key={cat} className="mb-2">
              <div className="text-[10px] font-semibold text-blue-400 mb-1">{cat}</div>
              {typeof props === 'object' && props !== null
                ? Object.entries(props).map(([k, v]) => (
                    <div key={k} className="flex justify-between py-0.5 text-[11px] border-b border-border/30">
                      <span className="text-muted-foreground truncate max-w-[120px]">{k}</span>
                      <span className="truncate max-w-[130px] text-right" title={String(v)}>{String(v)}</span>
                    </div>
                  ))
                : <span className="text-[11px]">{String(props)}</span>
              }
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

function ActivityProperties({ detail }) {
  const fmtDate = (d) => d ? new Date(d).toLocaleDateString('en-GB', { day: '2-digit', month: 'short', year: '2-digit' }) : '\u2014';
  return (
    <div className="space-y-3" data-testid="activity-detail">
      <div className="flex items-center gap-2">
        <div className="w-3 h-3 rounded-full" style={{ backgroundColor: STATUS_COLORS[detail.scheduleStatus] || '#6B7280' }} />
        <h4 className="font-semibold text-sm">{detail.activityName}</h4>
      </div>
      <div className="space-y-1 text-xs">
        <PropRow label="WBS Code" value={detail.activityCode} />
        <PropRow label="Duration" value={`${Math.round((detail.durationDays || 0) / 8)}d`} />
        <PropRow label="Start" value={fmtDate(detail.plannedStart)} />
        <PropRow label="Finish" value={fmtDate(detail.plannedFinish)} />
        <PropRow label="Progress" value={`${detail.physicalProgressPct || 0}%`} />
        <PropRow label="Float" value={detail.totalFloat ?? '\u2014'} />
        <PropRow label="Mapped Objects" value={detail.modelObjects?.length || 0} />
        <div className="flex justify-between py-1 border-b border-border/50">
          <span className="text-muted-foreground">Status</span>
          <Badge variant="outline" className="text-[10px] h-5"
            style={{ borderColor: STATUS_COLORS[detail.scheduleStatus] || '#6B7280', color: STATUS_COLORS[detail.scheduleStatus] || '#6B7280' }}>
            {(detail.scheduleStatus || '').replace('_', ' ')}
          </Badge>
        </div>
      </div>
      {detail.modelObjects?.length > 0 && (
        <div className="mt-2">
          <h5 className="text-xs font-semibold mb-1 text-muted-foreground">Linked 3D Objects</h5>
          {detail.modelObjects.map(o => (
            <div key={o.dbId} className="text-[11px] py-0.5 flex items-center gap-1">
              <span className="font-mono text-muted-foreground">#{o.dbId}</span>
              <span className="truncate">{o.name || o.objectName || ''}</span>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

function PropRow({ label, value }) {
  return (
    <div className="flex justify-between py-1 border-b border-border/50">
      <span className="text-muted-foreground">{label}</span>
      <span>{value}</span>
    </div>
  );
}

export function ObjectTreePanel({
  modelId, projectId,
  objectTreeRoots, flatObjectTreeLength, objectTreeTotal,
  loadingTree, objectTreeSearch, onSearchChange, onSearch,
  expandedTreeNodes, onToggle, selectedTreeDbId, onSelect, highlightedTreeDbId,
  selectedObjectDetail, selectedActivityDetail, onLocateInViewer
}) {
  const PROJECT_ID = projectId;
  const handleExportTree = () => {
    if (!modelId) return;
    const a = document.createElement('a');
    a.href = `${API}/api/v1/projects/${PROJECT_ID}/4d/models/${modelId}/object-tree/export`;
    a.download = 'Object_Tree.xlsx';
    a.click();
    toast.success('Exporting object tree with properties...');
  };

  return (
    <div className="flex flex-row h-full" data-testid="object-tree-tab">
      {/* LEFT: Tree */}
      <div className="flex flex-col flex-1 min-w-0 border-r">
        <div className="flex items-center gap-1 px-2 py-1 border-b shrink-0">
          <Search className="w-3 h-3 text-muted-foreground" />
          <Input
            placeholder="Search objects..."
            value={objectTreeSearch}
            onChange={e => onSearchChange(e.target.value)}
            onKeyDown={e => { if (e.key === 'Enter') onSearch(); }}
            className="h-6 text-xs border-0 shadow-none focus-visible:ring-0"
            data-testid="object-search"
          />
          <Button variant="ghost" size="sm" className="h-6 text-[10px]" onClick={onSearch} disabled={loadingTree}>
            {loadingTree ? <Loader2 className="w-3 h-3 animate-spin" /> : <Search className="w-3 h-3" />}
          </Button>
          <span className="text-[10px] text-muted-foreground whitespace-nowrap">{objectTreeTotal} obj</span>
          <Button variant="ghost" size="sm" className="h-6 text-[10px] px-1.5" onClick={handleExportTree} title="Export object tree with properties to Excel" data-testid="export-object-tree-btn" disabled={!modelId || flatObjectTreeLength === 0}>
            <Download className="w-3 h-3" />
          </Button>
        </div>
        <div className="flex-1 overflow-auto px-1 py-1" data-testid="object-tree-list">
          {flatObjectTreeLength === 0 && !loadingTree && (
            <div className="text-xs text-muted-foreground text-center pt-8 space-y-2">
              <Box className="w-8 h-8 mx-auto text-muted-foreground/40" />
              <p>No objects found.</p>
              <p className="text-[10px]">Object tree populates after model translation.</p>
            </div>
          )}
          {loadingTree && (
            <div className="text-xs text-muted-foreground text-center pt-8">
              <Loader2 className="w-5 h-5 mx-auto animate-spin mb-2" />Loading...
            </div>
          )}
          {objectTreeRoots.map(root => (
            <ObjectTreeNode
              key={root.dbId}
              node={root}
              depth={0}
              expandedNodes={expandedTreeNodes}
              onToggle={onToggle}
              selectedDbId={selectedTreeDbId}
              onSelect={onSelect}
              highlightedDbId={highlightedTreeDbId}
            />
          ))}
        </div>
      </div>

      {/* RIGHT: Properties */}
      <div className="flex flex-col w-[320px] min-w-[240px] shrink-0 overflow-auto p-3" data-testid="properties-panel">
        {selectedObjectDetail ? (
          <div>
            <ObjectProperties detail={selectedObjectDetail} />
            <Button variant="outline" size="sm" className="w-full h-7 text-xs mt-3"
              onClick={() => onLocateInViewer(selectedObjectDetail.dbId)} data-testid="locate-in-viewer-btn">
              <LocateFixed className="w-3 h-3 mr-1" />Locate in 3D Viewer
            </Button>
          </div>
        ) : selectedActivityDetail ? (
          <ActivityProperties detail={selectedActivityDetail} />
        ) : (
          <div className="text-xs text-muted-foreground text-center pt-8 space-y-2">
            <Crosshair className="w-8 h-8 mx-auto text-muted-foreground/30" />
            <p>Select an object in the 3D viewer or tree to see properties</p>
            <p className="text-[10px]">Click an activity row for schedule details</p>
          </div>
        )}
      </div>
    </div>
  );
}
